import { useMemo } from 'react';

import { useAutoDismissalCheck } from './auto-dismissal-store.ts';
import { isFeatureEnabled, type FeatureName } from './messages.ts';
import { useIsMessageSeen } from './state.ts';
import { useChangeboardingMessage } from './use-changeboarding-messages.tsx';

/**
 * useIsChangeboardingActive is a hook that returns whether the changeboarding
 * experience is active for a given feature.
 * @param feature - The feature name to check if the changeboarding experience is active for
 * @returns Whether the changeboarding experience is active for the given feature
 * The hook returns true only when:
 * - Feature is enabled (isFeatureEnabled)
 * - Message hasn't been seen/dismissed (!isMessageSeen)
 * - Loading is complete (!loading)
 * - Placement props are available (!!placementProps) - this means the API returned this feature as the active one
 * - Instance ID is available (!!instanceId)
 * - Auto-dismissal thresholds have not been exceeded
 */
export const useIsChangeboardingActive = (feature: FeatureName): boolean => {
  const featureEnabled = isFeatureEnabled(feature);
  const isMessageSeen = useIsMessageSeen(feature);

  const { loading, placementProps, instanceId } = useChangeboardingMessage(
    feature,
    isMessageSeen ?? false,
    !featureEnabled,
  );

  const skipAutoDismissalCheck = useMemo(
    () =>
      !featureEnabled || !!(isMessageSeen || loading || !placementProps || !instanceId),
    [featureEnabled, isMessageSeen, loading, placementProps, instanceId],
  );

  const { loading: autoDismissalLoading, result: autoDismissalResult } =
    useAutoDismissalCheck(feature, skipAutoDismissalCheck);

  const shouldAutoDismiss = autoDismissalResult?.shouldAutoDismiss ?? false;

  return (
    featureEnabled &&
    !isMessageSeen &&
    !loading &&
    !!placementProps &&
    !!instanceId &&
    !autoDismissalLoading &&
    !shouldAutoDismiss
  );
};
